import React, { useState } from 'react';
import apiClient from '../../Shared/services/api';

interface BillingWarningBannerProps {
  show: boolean;
  variant?: 'payment' | 'trial';
  trialEndsAt?: string | null;
}

const formatDate = (isoDate: string): string =>
  new Date(isoDate).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });

const BillingWarningBanner: React.FC<BillingWarningBannerProps> = ({
  show,
  variant = 'payment',
  trialEndsAt,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleUpdatePayment = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const { portal_url } = await apiClient.getBillingPortalUrl();
      window.location.href = portal_url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) {
    return null;
  }

  const message =
    variant === 'trial'
      ? `Your free trial ends on ${
          trialEndsAt ? formatDate(trialEndsAt) : 'soon'
        }. Add a payment method to keep access after your trial.`
      : 'Your payment failed. Please update your payment method to avoid losing access.';

  const ctaLabel = variant === 'trial' ? 'Add Payment Method' : 'Update Payment Method';

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <p className="text-sm text-amber-800">{message}</p>
      <button
        type="button"
        onClick={() => void handleUpdatePayment()}
        disabled={isLoading}
        className="shrink-0 text-sm font-medium text-amber-900 underline hover:text-amber-700 disabled:opacity-50"
      >
        {isLoading ? 'Loading...' : ctaLabel}
      </button>
    </div>
  );
};

export default BillingWarningBanner;
